import { head } from 'ramda'
import type { AppSelector } from 'app/store'
import getDataSelector from 'core/utils/getDataSelector'
import { createSharedSelector } from 'core/utils/selectorHelpers'
import DataKeys from 'k8s/DataKeys'
import type { IClusterUpgradeJobSelector } from '../cluster-upgrade/model'
import { kaapiClusterUpgradeJobsByClusterSelector } from '../cluster-upgrade/selectors'
import { kaapiEventsSelector } from '../events/selectors'
import { hostedControlPlaneSelector } from '../hosted-control-plane/selectors'
import { kaapiKubeadmControlPlanesSelector } from '../kubeadm-control-plane/selectors'
import { kaapiMachineDeploymentsSelector } from '../machine-deployment/selectors'
import { kaapiMachinesSelector } from '../machines/selectors'

export const checkIsBYOHCluster = (cluster) =>
  cluster?.spec?.infrastructureRef?.kind === 'ByoCluster'

export const infrastructureKind = (cluster) => {
  if (cluster?.spec?.infrastructureRef?.kind === 'ByoCluster') {
    return 'BYOH'
  }
  if (cluster?.spec?.infrastructureRef?.kind === 'OpenStackCluster') {
    return 'PCD Virtualization'
  }
  return '-'
}

export const kaapiClustersSelector: AppSelector<any[]> = createSharedSelector(
  getDataSelector<DataKeys.KaapiClusters>(DataKeys.KaapiClusters),
  getDataSelector<DataKeys.KaapiClusterAddons>(DataKeys.KaapiClusterAddons),
  (kaapiClusters, kaapiClusterAddons): any[] => {
    return kaapiClusters.map((cluster) => {
      const metalLBAddonLocal = kaapiClusterAddons?.find(
        (addon) =>
          addon?.spec?.clusterID === cluster?.metadata?.name && addon?.spec?.type === 'metallb',
      )
      return {
        ...cluster,
        isBYOHCluster: checkIsBYOHCluster(cluster),
        metalLBAddon: metalLBAddonLocal || {},
        isMetalLBAddon: !!metalLBAddonLocal,
      }
    })
  },
)

//TODO:: Once upgradejob api impleneted need to refactor following logic
// export const clusterUpgradeJobsByClusterSelector = createSharedSelector(
// 	clusterUpgradeJobsSelector,
// 	(upgradeJobs) =>
// 		upgradeJobs.reduce((accum, job) => {
// 			const clusterName = job?.spec?.clusterName || ''
// 			const existingUpgradeJobs = accum[clusterName] || []
// 			// Sort by job creation timestamp in descending order
// 			const allUpgradeJobs = [...existingUpgradeJobs, job]
// 				.sort(compareByCreationTimestamp)
// 				.reverse()
// 			return assocPath([clusterName], allUpgradeJobs, accum)
// 		}, emptyObj),
// )

// export const eksVersionsSelector = createSharedSelector(
//   getDataSelector<DataKeys.EksVersions>(DataKeys.EksVersions),
//   (versions: EKSClusterVersion[]): IEksVersionsSelector[] => {
//     return versions.map((version) => ({
//       ...version,
//       deprecated: version.spec?.deprecated,
//       name: version.metadata?.name,
//     }))
//   },
// )

// export const clusterVersionsSelector = createSharedSelector(
//   getDataSelector<DataKeys.ClusterVersions>(DataKeys.ClusterVersions),
//   (rawClusterVersions): any[] => {
//     return rawClusterVersions.map((rawClusterVersion) => {
//       const latest = rawClusterVersion?.metadata?.labels?.['pmk.pf9.io/latest'] === 'true'
//       return {
//         ...rawClusterVersion,
//         latest: latest,
//         clusterCreationAllowed:
//           rawClusterVersion?.phase === 'EarlyAccess' ||
//           (rawClusterVersion?.phase === 'Active' && latest) ||
//           (rawClusterVersion?.phase === 'Deprecated' && latest),
//         // Cluster can be upgraded to this version from a lower version or patch
//         clusterUpgradePossible:
//           rawClusterVersion?.phase === 'EarlyAccess' ||
//           rawClusterVersion?.phase === 'Active' ||
//           (rawClusterVersion?.phase === 'Deprecated' && latest),
//       }
//     })
//   },
// )

// const getLatestClusterVersion = (versions, versionPropKey): string => {
//   if (isNilOrEmpty(versions)) return null
//   const sortedVersions = sortWith([descend(prop(versionPropKey))])(versions)
//   return sortedVersions[0][versionPropKey] as string
// }

// const filterClusterUpgradePossible = (clusterVersion) => clusterVersion?.clusterUpgradePossible

export const kaapiClustersOverviewSelector: AppSelector<any[]> = createSharedSelector(
  getDataSelector<DataKeys.KaapiClusters>(DataKeys.KaapiClusters),
  hostedControlPlaneSelector,
  kaapiMachineDeploymentsSelector,
  kaapiMachinesSelector,
  kaapiClusterUpgradeJobsByClusterSelector,
  kaapiKubeadmControlPlanesSelector,
  getDataSelector<DataKeys.KaapiClusterAddons>(DataKeys.KaapiClusterAddons),
  kaapiEventsSelector,
  //  clusterUpgradeJobsByClusterSelector,
  // clusterVersionsSelector,
  (
    kaapiClusters,
    hostedControlPlanes,
    kaapiMachineDeployments,
    kaapiMachines,
    kaapiClusterUpgradeJobs,
    kubeadmControlPlanes,
    kaapiClusterAddons,
    kaapiEvents,
    //  clusterUpgradeJobsByCluster,
    // clusterVersions,
  ): any[] => {
    return kaapiClusters.map((cluster) => {
      const name = cluster?.metadata?.name

      // CAPO writes no status field for cluster-infra failures like
      // security-group quota - the OpenStackCluster CR only ever carries
      // network + ready, and the real cause lives solely in a recurring
      // Warning Event on that CR. The OpenStackCluster object itself is not
      // reachable through the tenant proxy, but its Events are, and the
      // Cluster's own spec.infrastructureRef.name IS the OpenStackCluster
      // name - so match the event by that, no OpenStackCluster fetch needed.
      const infraName = cluster?.spec?.infrastructureRef?.name
      const infraFailureEvent = kaapiEvents
        ?.filter(
          (e) =>
            e?.type === 'Warning' &&
            e?.involvedObject?.kind === 'OpenStackCluster' &&
            e?.involvedObject?.name === infraName,
        )
        ?.sort((a, b) => Date.parse(b?.lastTimestamp || 0) - Date.parse(a?.lastTimestamp || 0))?.[0]
      // Find the kubeadm control plane for the cluster
      const kubeadmControlPlane = kubeadmControlPlanes?.find(
        (h) => h?.metadata?.name === cluster?.spec?.controlPlaneRef?.name,
      )
      // Find the hosted control plane for the cluster
      const hostedControlPlane = hostedControlPlanes?.find(
        (h) => h?.metadata?.name === cluster?.spec?.controlPlaneRef?.name,
      )
      // Find the machine deployments for the cluster
      const machineDeployments = kaapiMachineDeployments?.filter(
        (md) => md?.spec?.clusterName === cluster?.metadata?.name,
      )

      // Find the machines for the cluster
      const machines = kaapiMachines?.filter(
        (h) => h?.spec?.clusterName === cluster?.metadata?.name,
      )

      // Find the addon for the cluster
      const metalLBAddonLocal = kaapiClusterAddons?.find(
        (addon) =>
          addon?.spec?.clusterID === cluster?.metadata?.name && addon?.spec?.type === 'metallb',
      )

      // Find Control Plane Cluster Class
      const controlPlaneClusterClass =
        hostedControlPlane?.spec?.hcpClass || cluster?.spec?.topology?.class

      // Upgrade
      const isBYOHCluster = checkIsBYOHCluster(cluster)
      const infrastructureProvider = infrastructureKind(cluster)
      const uid = cluster?.metadata?.uid
      const upgradeJobs = kaapiClusterUpgradeJobs[name] || []
      const clusterName = cluster?.metadata?.name
      const clusterPhase = cluster?.status?.phase
      const controlPlaneType =
        !hostedControlPlane && !kubeadmControlPlane
          ? '-' // edge case where neither hosted nor kubeadm (shouldn't occur in prod)
          : kubeadmControlPlane
            ? 'Colocated'
            : 'Managed'
      const workerNodeCount = (machineDeployments || []).reduce(
        (total, md) => total + (md?.spec?.replicas || 0),
        0,
      )
      const hasEnoughControlPlaneNodes =
        controlPlaneType === 'Managed' || cluster?.spec?.topology?.controlPlane?.replicas >= 3
      const hasEnoughWorkerNodes = workerNodeCount >= 2
      const isHighlyAvailable = hasEnoughControlPlaneNodes && hasEnoughWorkerNodes ? 'Yes' : 'No'
      const k8Version = hostedControlPlane?.spec?.version || kubeadmControlPlane?.spec?.version
      // const canUpgrade =
      //   compareVersions(version, version ? latestEksClusterVersion : latestAwsClusterVersion) === -1
      const canUpgrade = true
      //  const upgradeJobs =
      //    clusterUpgradeJobsByCluster[hostedControlPlane?.metadata?.name] || []
      // upgrade jobs are sorted in descending order by creating timestamp
      // in clusterUpgradeJobsByCluster selector. Newest job is at the top
      const recentUpgradeJob =
        upgradeJobs.length > 0 ? head<IClusterUpgradeJobSelector>(upgradeJobs) : null
      const upgradeFailed = recentUpgradeJob?.phase === 'Failed'
      const upgradeCompleted = recentUpgradeJob?.phase === 'Completed'
      const currentUpgradeJob = upgradeFailed || upgradeCompleted ? null : recentUpgradeJob
      const upgrading = !!currentUpgradeJob
      const upgradeSucessCount = 13
      const upgradeFailedCount = 12
      const strategy = machineDeployments[0]?.spec?.strategy?.type
      const maxSurgeValue = machineDeployments[0]?.spec?.strategy?.rollingUpdate?.maxSurge
      const maxSurge = 'Count'
      const maxUnavailableValue =
        machineDeployments[0]?.spec?.strategy?.rollingUpdate?.maxUnavailable
      const maxUnavailable = 'Count'
      return {
        ...cluster,
        uid,
        k8Version,
        clusterName,
        clusterPhase,
        controlPlaneType,
        isHighlyAvailable,
        canUpgrade,
        recentUpgradeJob,
        upgradeFailed,
        upgradeCompleted,
        currentUpgradeJob,
        upgrading,
        upgradeSucessCount,
        upgradeFailedCount,
        strategy,
        maxSurgeValue,
        maxUnavailableValue,
        maxSurge,
        maxUnavailable,
        hostedControlPlane,
        kubeadmControlPlane,
        machineDeployments,
        machines,
        infraFailureEvent,
        infrastructureProvider,
        upgradeJobs,
        isBYOHCluster,
        metalLBAddon: metalLBAddonLocal || {},
        isMetalLBAddon: !!metalLBAddonLocal,
        controlPlaneClusterClass,
      }
    })
  },
)
